// services/adminPushService.ts
//
// Notifications push web des administrateurs : enregistrement du token FCM du
// navigateur auprès du backend, et réception des messages au premier plan.
import axios from "axios";
import { initializeApp, getApp, getApps, type FirebaseApp } from "firebase/app";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { BACKEND_URL } from "../../config/api";

const NOTIFICATION_SOUND_URL = "/sounds/new-message.wav";
const NOTIFICATION_ICON_URL = "/logo.png";
const SERVICE_WORKER_PATH = "/firebase-messaging-sw.js";

// Valeurs publiques du projet Firebase `easylife-d9070` (app web deja
// enregistree). Le .env de production n'est pas rsyncé au deploy : ces
// fallbacks garantissent que le build Docker active quand meme les push.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyBr6T1HvMezzxFnprqT59mBChsFzWwkHoM",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "easylife-d9070.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "easylife-d9070",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "easylife-d9070.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "922312304870",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:922312304870:web:edfa03c42db98555d9f327",
};

const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

/** Résultat de la tentative d'activation des notifications. */
export type AdminPushStatus = "granted" | "denied" | "unsupported" | "not-configured" | "error";

export interface AdminPushMessage {
  title: string;
  body: string;
  userId: string | null;
}

/** Les variables d'environnement Firebase sont-elles toutes renseignées ? */
export function isAdminPushConfigured(): boolean {
  return Boolean(
    firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.messagingSenderId && firebaseConfig.appId
  );
}

function getFirebaseApp(): FirebaseApp {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

/**
 * URL du service worker, configuration Firebase incluse.
 *
 * Le service worker est un fichier statique de `public/` : il ne traverse pas
 * Vite et ne peut donc pas lire `import.meta.env`. On lui passe la config en
 * query string, qu'il relit via `new URL(self.location).searchParams`.
 */
function serviceWorkerUrl(): string {
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey ?? "",
    authDomain: firebaseConfig.authDomain ?? "",
    projectId: firebaseConfig.projectId ?? "",
    storageBucket: firebaseConfig.storageBucket ?? "",
    messagingSenderId: firebaseConfig.messagingSenderId ?? "",
    appId: firebaseConfig.appId ?? "",
  });
  return `${SERVICE_WORKER_PATH}?${params.toString()}`;
}

let registeredToken: string | null = null;

/**
 * Demande l'autorisation, récupère le token FCM du navigateur et l'enregistre
 * côté backend. Idempotent : réappeler la fonction ne crée pas de doublon, le
 * backend faisant un upsert sur le token.
 */
export async function registerAdminPush(authToken: string): Promise<AdminPushStatus> {
  if (!isAdminPushConfigured()) return "not-configured";
  if (!("serviceWorker" in navigator) || !("Notification" in window)) return "unsupported";

  try {
    if (!(await isSupported())) return "unsupported";

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return "denied";

    const registration = await navigator.serviceWorker.register(serviceWorkerUrl());
    const tokenOptions: { vapidKey?: string; serviceWorkerRegistration: ServiceWorkerRegistration } = {
      serviceWorkerRegistration: registration,
    };
    // La clé VAPID publique n'est pas obligatoire : FCM en genere une par
    // defaut pour le projet. On ne la passe que si elle est configuree.
    if (vapidKey) tokenOptions.vapidKey = vapidKey;
    const token = await getToken(getMessaging(getFirebaseApp()), tokenOptions);

    if (!token) return "error";

    await axios.post(
      `${BACKEND_URL}/notifications/admin/web-token`,
      { token },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );

    registeredToken = token;
    return "granted";
  } catch (error) {
    console.error("Activation des notifications admin impossible:", error);
    return "error";
  }
}

/** Révoque le token courant côté backend (déconnexion). */
export async function unregisterAdminPush(authToken: string): Promise<void> {
  if (!registeredToken) return;
  try {
    await axios.delete(`${BACKEND_URL}/notifications/admin/web-token`, {
      headers: { Authorization: `Bearer ${authToken}` },
      data: { token: registeredToken },
    });
    registeredToken = null;
  } catch (error) {
    console.error("Révocation du token push admin impossible:", error);
  }
}

/**
 * Écoute les messages reçus **dashboard au premier plan**. Dans ce cas FCM
 * n'affiche rien de lui-même : c'est à l'application de réagir.
 *
 * Renvoie une fonction de désabonnement.
 */
export function onAdminPushMessage(handler: (message: AdminPushMessage) => void): () => void {
  if (!isAdminPushConfigured()) return () => {};

  try {
    return onMessage(getMessaging(getFirebaseApp()), (payload) => {
      const data = payload.data ?? {};
      const notification = payload.notification ?? {};
      handler({
        title: data.title || notification.title || "Nouveau message support",
        body: data.body || notification.body || "",
        userId: data.user_id ?? null,
      });
    });
  } catch (error) {
    console.error("Écoute des notifications au premier plan impossible:", error);
    return () => {};
  }
}

// ---------------------------------------------------------------------------
// Audio
// ---------------------------------------------------------------------------

let notificationAudio: HTMLAudioElement | null = null;
let audioUnlocked = false;
let pendingSound = false;

// --- État audio observable ------------------------------------------------
type AudioStatusListener = (unlocked: boolean) => void;
const audioStatusListeners = new Set<AudioStatusListener>();

export function isNotificationAudioUnlocked(): boolean {
  return audioUnlocked;
}

export function subscribeNotificationAudioStatus(listener: AudioStatusListener): () => void {
  audioStatusListeners.add(listener);
  // Émet immédiatement l'état courant pour que l'abonné soit synchrone.
  listener(audioUnlocked);
  return () => audioStatusListeners.delete(listener);
}

function emitAudioStatus(): void {
  for (const listener of audioStatusListeners) {
    try {
      listener(audioUnlocked);
    } catch (e) {
      console.error("Listener audio en erreur:", e);
    }
  }
}

function ensureNotificationAudio(): HTMLAudioElement {
  if (!notificationAudio) {
    notificationAudio = new Audio(NOTIFICATION_SOUND_URL);
    notificationAudio.preload = "auto";
  }
  return notificationAudio;
}

/**
 * Précharge le fichier audio dès le montage du hook et **teste silencieusement**
 * si l'audio est déjà déverrouillé. Chrome ne fournit aucune API pour lire cet
 * état : la seule façon de savoir, c'est d'essayer un `play()` muet et de voir
 * si ça lève `NotAllowedError`.
 *
 * - Si ça réussit → `audioUnlocked = true`, la bannière ne s'affichera pas.
 * - Si ça échoue → `audioUnlocked = false`, la bannière s'affichera.
 */
export function preloadNotificationAudio(): void {
  const audio = ensureNotificationAudio();

  // Test silencieux : on ne veut pas déclencher de son, juste savoir si
  // Chrome autorise le play(). On met le volume à 0 le temps du test.
  const previousVolume = audio.volume;
  audio.volume = 0;
  audio
    .play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = previousVolume;
      audioUnlocked = true;
      emitAudioStatus();
    })
    .catch(() => {
      audio.volume = previousVolume;
      audioUnlocked = false;
      emitAudioStatus();
    });
}

/**
 * Déverrouille l'autoplay audio après un geste utilisateur.
 *
 * Sans cela, Chrome bloque le son de la première notification avec
 * `NotAllowedError`. On force un `play()` muet, puis on remet le volume.
 * Si un son était en attente (notification reçue avant tout geste), on le
 * joue immédiatement après le déverrouillage.
 */
export function unlockNotificationAudio(): void {
  if (audioUnlocked) return;
  const audio = ensureNotificationAudio();
  const previous = audio.volume;
  audio.volume = 0;
  audio
    .play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = previous;
      audioUnlocked = true;
      emitAudioStatus();
      if (pendingSound) {
        pendingSound = false;
        playNotificationSound();
      }
    })
    .catch(() => {
      audio.volume = previous;
    });
}

/**
 * Joue le son de nouveau message.
 *
 * Si l'autoplay est bloqué (aucun geste utilisateur depuis le chargement de la
 * page), on mémorise qu'un son est en attente : il sera joué dès que
 * `unlockNotificationAudio` réussira.
 *
 * En arrière-plan, c'est le système d'exploitation qui émet son propre son via
 * la notification du service worker : les navigateurs ne permettent pas de son
 * personnalisé pour une notification web. Ce son-ci couvre le cas où le
 * dashboard est au premier plan.
 */
export function playNotificationSound(): void {
  const audio = ensureNotificationAudio();
  audio.currentTime = 0;
  audio.volume = 1;
  audio.play().catch((e: DOMException) => {
    if (e.name === "NotAllowedError") {
      pendingSound = true;
    } else {
      console.error("Lecture du son de notification impossible:", e);
    }
  });
}

/** Affiche une notification système alors que le dashboard est au premier plan. */
export async function showForegroundNotification(message: AdminPushMessage): Promise<void> {
  if (!("serviceWorker" in navigator) || Notification.permission !== "granted") return;
  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(message.title, {
      body: message.body,
      icon: NOTIFICATION_ICON_URL,
      badge: NOTIFICATION_ICON_URL,
      tag: message.userId ? `support-chat-${message.userId}` : "support-chat",
      // Le son custom est joué par `playNotificationSound`. `silent: true`
      // évite que le navigateur superpose le son système par-dessus.
      silent: true,
      data: {
        link: message.userId ? `/support/chat?user=${message.userId}` : "/support/chat",
      },
    });
  } catch (error) {
    console.error("Affichage de la notification impossible:", error);
  }
}
